import { useMemo } from 'react';
import { useTranslate } from '@shopify/ui-extensions-react/customer-account';
import {
  generateSuccessMessage,
  generateUploadingText,
  generateAllPhotosUploadedMessage,
} from '../utils/calculations';
import { TRANSLATION_KEYS } from '../config/constants';
import { UseModalTextProps, UseModalTextReturn } from '../types';

/**
 * Custom hook for managing modal text and translations
 * @param props - Configuration object with upload state
 * @returns All text strings for the modal UI
 */
export const useModalText = ({
  uploadResultsCount,
  uploadProgress,
  totalPhotosRequired
}: UseModalTextProps): UseModalTextReturn => {
  const translate = useTranslate();

  const modalTitle = translate(TRANSLATION_KEYS.MODAL.TITLE);
  const loadingText = translate('orderDetails.loading');
  const noPhotosRequiredTitle = translate('noPhotosRequired.title');
  const noPhotosRequiredMessage = translate('noPhotosRequired.message');
  const cancelButtonText = translate(TRANSLATION_KEYS.MODAL.CANCEL);
  const submitButtonText = translate(TRANSLATION_KEYS.MODAL.SUBMIT);

  const allPhotosUploadedTitle = 'All required photos uploaded!';

  const allPhotosUploadedMessage = useMemo(() => {
    return generateAllPhotosUploadedMessage(totalPhotosRequired);
  }, [totalPhotosRequired]);

  const successMessage = useMemo(() => {
    return generateSuccessMessage(uploadResultsCount, translate);
  }, [translate, uploadResultsCount]);

  const uploadingButtonText = useMemo(() => {
    return generateUploadingText(uploadProgress, translate);
  }, [translate, uploadProgress]);

  return {
    modalTitle,
    loadingText,
    noPhotosRequiredTitle,
    noPhotosRequiredMessage,
    allPhotosUploadedTitle,
    allPhotosUploadedMessage,
    cancelButtonText,
    submitButtonText,
    uploadingButtonText,
    successMessage,
  };
};